/**
 * Compact "Xero" state pill for an invoiced order card.
 *
 * States:
 *   • Pushed OK → green pill with the Xero invoice number, click opens the
 *     Xero deep-link in a new tab.
 *   • Not pushed yet + no error → subtle amber "Pushing…" pill (auto-push is
 *     still in-flight, so this is transient).
 *   • Errored → red pill with the failure snippet as a tooltip + a Retry
 *     button that hits POST /api/xero/invoices/retry.
 *   • Xero not connected → grey "Xero off" pill, no interaction.
 *
 * Kept intentionally small so it can drop into the Staff Orders Queue cards
 * and the various order-detail modals without adding weight.
 */
import React, { useState } from 'react';
import { ExternalLink, RefreshCw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * @param {Object}   props
 * @param {Object}   props.order    — order doc (must expose xero_invoice_id / xero_invoice_url / xero_last_error).
 * @param {string}   props.source   — source key: `installer_material_orders`, `proepoxy`, `prodecor`, `probound`, etc.
 * @param {Function} [props.onRetried] — callback fired after a successful retry so the parent can refetch.
 */
export default function XeroInvoiceBadge({ order, source, onRetried }) {
  const [retrying, setRetrying] = useState(false);

  if (!order) return null;
  const invoiceId = order.xero_invoice_id;
  const invoiceUrl = order.xero_invoice_url;
  const invoiceNumber = order.xero_invoice_number;
  const err = order.xero_last_error;
  const status = order.xero_invoice_status;

  const stopClicks = (e) => { e.stopPropagation(); e.preventDefault(); };

  const retry = async (e) => {
    stopClicks(e);
    setRetrying(true);
    try {
      const res = await fetch(`${API_URL}/api/xero/invoices/retry`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source, order_id: order.id }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || 'Retry failed');
      if (body.status === 'created' || body.status === 'already_pushed') {
        toast.success(`Xero draft invoice ${body.invoice_number || ''} created`);
      } else if (body.status === 'skipped') {
        toast.warning(body.error === 'xero_not_connected'
          ? 'Xero is not connected — connect from Staff → Xero first.'
          : `Skipped: ${body.error || 'no reason given'}`);
      } else {
        toast.error(body.error || 'Push failed — check Xero logs');
      }
      if (onRetried) onRetried();
    } catch (e2) {
      toast.error(e2.message);
    } finally {
      setRetrying(false);
    }
  };

  // 1. Already pushed → show the invoice number + deep-link.
  if (invoiceId) {
    const paid = status === 'PAID' || order.paid_via === 'xero';
    const part = !paid && Number(order.xero_amount_paid) > 0;
    const overdue = !paid && order.xero_overdue;
    const cls = paid ? 'bg-emerald-600 text-white border-emerald-700 hover:bg-emerald-500' : overdue ? 'bg-rose-100 text-rose-800 border-rose-300 hover:bg-rose-200' : part ? 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200' : 'bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200';
    const label = paid ? `Paid in Xero${invoiceNumber ? ` · ${invoiceNumber}` : ''}` : part ? `£${Number(order.xero_amount_paid).toFixed(0)} of £${Number(order.xero_total || 0).toFixed(0)} received` : overdue ? `Overdue · ${invoiceNumber || 'Xero'}` : (invoiceNumber || 'View in Xero');
    return (
      <a
        href={invoiceUrl || '#'}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        title={`Xero ${status || 'DRAFT'}${order.xero_due_date ? ` · due ${order.xero_due_date}` : ''} — click to open in Xero`}
        data-testid={`xero-open-${order.id}`}
        data-xero-state={paid ? 'paid' : overdue ? 'overdue' : part ? 'part' : 'open'}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-colors ${cls}`}
      >
        <ExternalLink className="w-3 h-3" />
        {label}
      </a>
    );
  }

  // 2. Error state → red pill + retry.
  if (err) {
    const isNoConn = err.toLowerCase().includes('not connected');
    return (
      <span
        onClick={stopClicks}
        title={err}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-100 text-rose-800 border border-rose-300"
        data-testid={`xero-error-${order.id}`}
      >
        <AlertCircle className="w-3 h-3" />
        {isNoConn ? 'Xero off' : 'Xero error'}
        {!isNoConn && (
          <button
            type="button"
            onClick={retry}
            disabled={retrying}
            data-testid={`xero-retry-${order.id}`}
            className="ml-1 hover:text-rose-950 disabled:opacity-50"
            title="Retry push"
          >
            <RefreshCw className={`w-3 h-3 ${retrying ? 'animate-spin' : ''}`} />
          </button>
        )}
      </span>
    );
  }

  // 3. In-flight — auto-push runs in the background, so a fresh Invoiced
  //    card can briefly land here before the pill flips to the green state.
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200"
      data-testid={`xero-pending-${order.id}`}
      title="Pushing DRAFT invoice to Xero…"
    >
      <RefreshCw className="w-3 h-3 animate-spin" />
      Xero…
    </span>
  );
}
